"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TechnicalStandards() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "01",
    title: "Diagnostica Strumentale",
    description: "Protocolli di ispezione video ad alta risoluzione per la mappatura dei depositi tecnici e strutturali. Analisi critica dei flussi per una pianificazione tecnico-specialistica della riqualificazione igienico-funzionale.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/video_ispezione_hvac.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/video_ispezione_hvac.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/video_ispezione_hvac.png-wvc-sizes",
    alt: "Ispezione video interna di condotti aria HVAC con strumentazione professionale"
  }, {
    number: "02",
    title: "Riqualificazione Tecnica",
    description: "Interventi di bonifica specialistica mediante sistemi robotizzati e tecnologie a spazzolatura meccanica. Ripristino certificato dell'efficienza aeraulica secondo i più rigorosi standard industriali B2B.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_pulizia_condotti.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_pulizia_condotti.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_pulizia_condotti.webp-wvc-sizes",
    alt: "Robot spazzolante per bonifica e pulizia interna di condotti aria"
  }, {
    number: "03",
    title: "Validazione e Compliance",
    description: "Emissione di dossier tecnici certificati con evidenze fotografiche macro e analisi post-intervento. Garanzia di conformità normativa per audit di sicurezza e manutenzione programmata evoluta.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/report_tecnico_certificato.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/report_tecnico_certificato.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/report_tecnico_certificato.png-wvc-sizes",
    alt: "Report fotografico tecnico con analisi prima e dopo intervento"
  }];
  return <section data-section-id="2989" id="standard-di-sicurezza-e-qualità" className="bg-background text-foreground py-32 md:py-44 lg:py-[200px] px-6 md:px-10 lg:px-16">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-8 md:mb-12" style={{
          color: "var(--secondary)"
        }}>
            Certificazione Tecnica — Metodo Operativo
          </p>
          <h2 className="font-default font-medium leading-[1.05] text-foreground max-w-[8/12] lg:max-w-[66.66%]" style={{
          fontSize: "clamp(48px, 6vw, 88px)",
          color: "var(--secondary)"
        }}>
            Precisione Tecnica Avanzata.
            <br />
            Risultati Certificati e Verificati.
          </h2>
        </div>

        <div ref={gridRef} className="mt-20 md:mt-28 lg:mt-[120px] grid grid-cols-1 md:grid-cols-3 relative">
          {services.map((service, i) => <div key={i} data-index={i} className={`group relative flex flex-col px-0 md:px-8 lg:px-10 py-12 md:py-0 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {i > 0 && <div className="hidden md:block absolute left-0 top-0 bottom-0 w-px" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />}
              {i > 0 && <div className="md:hidden absolute top-0 left-0 right-0 h-px" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />}

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{
            background: "radial-gradient(ellipse at center, var(--primary) 0%, transparent 70%)",
            mixBlendMode: "soft-light"
          }} aria-hidden="true" />

              <p className="font-mono text-xs tracking-[0.2em] mb-8 md:mb-10 relative z-10" style={{
            color: "var(--primary)"
          }}>
                {service.number}
              </p>

              <div className="relative overflow-hidden mb-8 md:mb-10 z-10 border border-border/40 shadow-lg">
                <div className="relative w-full" style={{
              paddingBottom: "133.33%"
            }}>
                  <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] brightness-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10" />
                </div>
                
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 z-20 bg-black/80">
                  <h3 className="font-default font-medium text-[28px] leading-tight mb-5" style={{
                color: "#FFFFFF"
              }}>
                    {service.title}
                  </h3>

                  <p className="font-default text-base leading-[1.7] mb-10 md:mb-12 flex-grow" style={{
                color: "#E5E7EB"
              }}>
                    {service.description}
                  </p>

                  <div className="mt-auto">
                    <Link to="/InspectionAndReportPage" className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase group/link" style={{
                  color: "var(--primary)"
                }}>
                      <span className="relative">
                        Approfondisci
                        <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
                      backgroundColor: "var(--primary)"
                    }} aria-hidden="true" />
                      </span>
                      <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-1" style={{
                    color: "var(--primary)"
                  }} strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}